import React, { useState, useEffect } from 'react';
import { api } from '../api';

function formatDate(d) {
  if (!d) return '–';
  const [y, m, day] = d.split('-');
  return `${day}.${m}.${y}`;
}

export default function KmVerlauf({ fahrzeugId, currentKm, onUpdate }) {
  const [verlauf, setVerlauf] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    km: '',
    datum: new Date().toISOString().slice(0, 10),
    notiz: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  const load = () => {
    setLoading(true);
    api.getKmVerlauf(fahrzeugId)
      .then(setVerlauf)
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(load, [fahrzeugId]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.km || !form.datum) { setError('km und Datum sind erforderlich.'); return; }
    setSaving(true);
    setError('');
    try {
      await api.addKmEintrag(fahrzeugId, { km: parseInt(form.km), datum: form.datum, notiz: form.notiz });
      setForm({ km: '', datum: new Date().toISOString().slice(0, 10), notiz: '' });
      setShowForm(false);
      load();
      if (onUpdate) onUpdate();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    await api.deleteKmEintrag(deleteId);
    setDeleteId(null);
    load();
    if (onUpdate) onUpdate();
  };

  // Compute km differences
  const verlaufMitDiff = verlauf.map((entry, i) => {
    const prev = verlauf[i + 1];
    const diff = prev ? entry.km - prev.km : null;
    return { ...entry, diff };
  });

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{
        padding: '16px 20px',
        borderBottom: '1px solid var(--gray-200)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--gray-600)' }}>
          Kilometerstand-Verlauf
          {currentKm && (
            <span style={{ marginLeft: 12, fontWeight: 700, fontSize: 15, color: 'var(--gray-900)' }}>
              {Number(currentKm).toLocaleString('de-DE')} km
            </span>
          )}
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowForm(s => !s)}>
          {showForm ? 'Abbrechen' : '+ Kilometerstand eintragen'}
        </button>
      </div>

      {showForm && (
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--gray-200)', background: 'var(--primary-light)' }}>
          {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}
          <form onSubmit={handleAdd}>
            <div style={{ display: 'grid', gridTemplateColumns: '160px 160px 1fr auto', gap: 12, alignItems: 'end' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Kilometerstand *</label>
                <input
                  className="form-input"
                  type="number"
                  min="0"
                  value={form.km}
                  onChange={e => setForm(f => ({ ...f, km: e.target.value }))}
                  placeholder="z.B. 125000"
                  autoFocus
                />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Datum *</label>
                <input
                  className="form-input"
                  type="date"
                  value={form.datum}
                  onChange={e => setForm(f => ({ ...f, datum: e.target.value }))}
                />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Notiz (optional)</label>
                <input
                  className="form-input"
                  value={form.notiz}
                  onChange={e => setForm(f => ({ ...f, notiz: e.target.value }))}
                  placeholder="z.B. Inspektion, Tankfüllung…"
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? '…' : 'Eintragen'}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 20, color: 'var(--gray-400)', fontSize: 13 }}>Lade…</div>
      ) : verlauf.length === 0 ? (
        <div style={{ padding: '24px 20px', color: 'var(--gray-400)', fontSize: 13, textAlign: 'center' }}>
          Noch keine Kilometerstand-Einträge vorhanden.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Datum</th>
                <th style={thStyle}>Kilometerstand</th>
                <th style={thStyle}>Differenz</th>
                <th style={thStyle}>Notiz</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {verlaufMitDiff.map((entry, i) => (
                <tr key={entry.id} style={i === 0 ? { background: 'var(--success-light)' } : {}}>
                  <td style={tdStyle}>{formatDate(entry.datum)}</td>
                  <td style={{ ...tdStyle, fontWeight: i === 0 ? 700 : 400, color: i === 0 ? 'var(--gray-900)' : 'var(--gray-700)' }}>
                    {Number(entry.km).toLocaleString('de-DE')} km
                    {i === 0 && <span className="badge badge-success" style={{ marginLeft: 8 }}>Aktuell</span>}
                  </td>
                  <td style={tdStyle}>
                    {entry.diff !== null ? (
                      <span style={{ color: 'var(--gray-500)', fontSize: 13 }}>
                        +{Number(entry.diff).toLocaleString('de-DE')} km
                      </span>
                    ) : '–'}
                  </td>
                  <td style={tdStyle}>{entry.notiz || '–'}</td>
                  <td style={tdStyle}>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => setDeleteId(entry.id)}
                      title="Eintrag löschen"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {deleteId && (
        <div className="modal-overlay" onClick={() => setDeleteId(null)}>
          <div className="modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Eintrag löschen</span>
              <button className="modal-close" onClick={() => setDeleteId(null)}>✕</button>
            </div>
            <div className="modal-body confirm-dialog">
              <p>Diesen Kilometerstand-Eintrag wirklich löschen?</p>
              <div className="confirm-actions">
                <button className="btn btn-secondary" onClick={() => setDeleteId(null)}>Abbrechen</button>
                <button className="btn btn-danger" onClick={handleDelete}>Löschen</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const thStyle = {
  textAlign: 'left', padding: '8px 14px',
  fontSize: 11, fontWeight: 600, color: 'var(--gray-500)',
  textTransform: 'uppercase', letterSpacing: '.5px',
  background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)'
};
const tdStyle = {
  padding: '10px 14px',
  borderBottom: '1px solid var(--gray-100)',
  color: 'var(--gray-700)', fontSize: 14
};
